import { useContext, useState, useMemo, useCallback } from 'react';
import { ChevronRight } from 'react-feather';
import styled from '@emotion/styled';
import { useFloating, useInteractions, useClick, useDismiss } from '@floating-ui/react';
import type { QuantAnalysisItem } from '@trinity/chat/api/interfaces/projects.quant-analysis';
import { QuantAnalysisFilterContext, cx } from '@trinity/chat-components';
import { AnalysisFilterMenu } from '@trinity/chat-components/components/QuantAnalysis/Analysis.FilterMenu.js';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis/Analysis.Table.js';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { BrandingProvider } from '@trinity/chat-components/containers/Branding/BrandingContainer.js';
import {
  allLeavesSelected,
  addLeafPaths,
  pathEquals,
  getDistinctNamesBFS,
} from '@trinity/chat-components/utils/nested-filter.js';
import {
  filterTableData,
  applyCustomNamesToFilteredData,
} from '@trinity/chat-components/utils/quant-analysis.js';
import { AnalysisContextMenu as ContextMenu } from './Analysis.ContextMenu';
import { QuantAnalysisFilterButton as FilterButton } from './FilterButton';
import { QuantAttributeTabButton } from './QuantAttributeTabButton';
import { QuantQuickChart } from './QuickChart';
import styles from './style/Analysis.Content.module.css';

type Props = {
  className?: string;
  value: QuantAnalysisItem;
};

export const AnalysisContent = (props: Props) => {
  const allFilterColumns = useMemo(
    () => getDistinctNamesBFS(props.value.data.columnNames),
    [props.value.data.columnNames]
  );
  const [quantFilter, setQuantFilter] = useContext(QuantAnalysisFilterContext);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [showAllButtons, setShowAllButtons] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: filterMenuOpen,
    onOpenChange: setFilterMenuOpen,
    placement: 'bottom-end',
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const setFloatingRef = useCallback(
    (node: HTMLElement | null) => {
      refs.setFloating(node);
    },
    [refs]
  );

  const setReferenceRef = useCallback(
    (node: Element | null) => {
      refs.setReference(node);
    },
    [refs]
  );

  const theming = useContext(AppThemingContext);

  const enabled = theming?.theme.enabled;
  const color = enabled ? theming?.palette?.primary?.main : '#28AAE1';

  const handleFilterChange = useCallback(
    (name: string) => {
      setQuantFilter((prev) => {
        const matchingPaths = props.value.data.columnNames.filter((p) => p.includes(name));
        if (allLeavesSelected(matchingPaths, prev.columnNames, [])) {
          return {
            ...prev,
            columnNames: prev.columnNames.filter((sp) => !matchingPaths.some((p) => pathEquals(sp, p))),
          };
        }
        return { ...prev, columnNames: addLeafPaths(prev.columnNames, matchingPaths, []) };
      });
    },
    [setQuantFilter, props.value.data.columnNames]
  );

  const columnButtons = useMemo(
    () =>
      allFilterColumns.map((name) => {
        const matchingPaths = props.value.data.columnNames.filter((p) => p.includes(name));
        return (
          <QuantAttributeTabButton
            key={name}
            name={name}
            selected={allLeavesSelected(matchingPaths, quantFilter.columnNames, [])}
            onClick={() => handleFilterChange(name)}
          />
        );
      }),
    [allFilterColumns, handleFilterChange, quantFilter.columnNames, props.value.data.columnNames]
  );
  const maxVisibleButtons = 7;

  const shouldShowToggle = columnButtons.length > maxVisibleButtons;
  const visibleButtons = showAllButtons ? columnButtons : columnButtons.slice(0, maxVisibleButtons);

  const handleToggleButtons = useCallback(() => {
    setShowAllButtons((prev) => !prev);
  }, []);

  return (
    <div className={cx(styles.root, props.className)}>
      <div className={styles.header}>
        <div className={styles.title}>{props.value.name}</div>
        <div className={styles.actions}>
          <ResetButton
            color={color}
            onClick={() =>
              setQuantFilter({
                columnNames: [],
                rowNames: [],
                sortConfig: null,
                selection: {
                  rows: new Set(),
                  columns: new Set(),
                  cells: new Map(),
                },
              })
            }
          >
            Reset
          </ResetButton>
          <FilterButtonContainer ref={setReferenceRef} {...getReferenceProps()}>
            <FilterButton onClick={() => setFilterMenuOpen((open) => !open)} color={color} />
          </FilterButtonContainer>
          <ContextMenu analysisItem={props.value} />
        </div>
        {filterMenuOpen && (
          <Portal>
            <div ref={setFloatingRef} style={{ ...floatingStyles, zIndex: 5 }} {...getFloatingProps()}>
              <AnalysisFilterMenu
                columnPaths={props.value.data.columnNames}
                rowPaths={props.value.data.rowNames}
                analysisName={props.value.name}
              />
            </div>
          </Portal>
        )}
      </div>

      <ButtonContainer>
        {visibleButtons}
        {shouldShowToggle && (
          <IndicatorIcon isExpanded={showAllButtons} onClick={handleToggleButtons} color={color} />
        )}
      </ButtonContainer>

      <BrandingProvider enabled={true}>
        <QuantQuickChart />
      </BrandingProvider>

      <AnalysisTable
        className={styles.table}
        data={(() => {
          const dataWithCustomNames = applyCustomNamesToFilteredData(
            props.value.data,
            props.value.data,
            quantFilter.customColumnNames,
            quantFilter.customRowNames
          );
          return filterTableData(dataWithCustomNames, quantFilter.columnNames, quantFilter.rowNames);
        })()}
        originalData={props.value.data}
      />

      <div className={styles.footer}>
        {props.value.footer.map((f) => (
          <div key={f} className={styles['footer-item']}>
            {f}
          </div>
        ))}
      </div>
    </div>
  );
};

const IndicatorIcon = ({
  size = 24,
  isExpanded = false,
  onClick,
  color,
}: {
  size?: number;
  isExpanded?: boolean;
  onClick?: () => void;
  color?: string;
}) => {
  return (
    <CardLink onClick={onClick} $isExpanded={isExpanded}>
      <ToggleText color={color}>{isExpanded ? 'Show less' : 'Show more'}</ToggleText>
      <StyledChevronRightIcon size={size} />
    </CardLink>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  gap: 4px;
`;

const ResetButton = styled.button<{ color: string }>(({ color }) => ({
  background: '#fff',
  color,
  fontWeight: 500,
  fontSize: '1rem',
  cursor: 'pointer',
  marginRight: '4px',
  border: 'none',
  padding: '4px 8px',
  borderRadius: '4px',
}));

const FilterButtonContainer = styled.span`
  display: flex;
  align-items: center;
`;

const StyledChevronRightIcon = styled(ChevronRight)<{ $isExpanded?: boolean }>(({ theme }) => ({
  color: theme.palette.sentiment.primary.main,
  paddingLeft: 2,
  strokeWidth: 3,
}));

const ToggleText = styled.span<{ color: string }>(({ color }) => ({
  fontSize: '0.875rem',
  color,
  marginRight: '4px',
  fontWeight: 500,
}));

const CardLink = styled.div<{ $isExpanded?: boolean }>(({ $isExpanded }) => ({
  borderRadius: '6px',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    '& svg': {
      transform: $isExpanded ? 'rotate(0deg)' : 'rotate(90deg)',
    },
  },
}));
